import {
  coerceInvoiceKind,
  coerceInvoiceStatus,
  type PlatformBillingInvoiceRow,
} from "../../../api/General/PlatformBilling";

/*
 * Shared vocabulary for the billing console.
 *
 * It lives in its own module because the sheet and the four invoice dialogs must name the same
 * document the same way. An invoice that reads "Manual #INV-0042" in the register and "Invoice 91"
 * in the dialog that settles it is the exact ambiguity a hand-collected payment cannot afford.
 */

/**
 * How a document is named, everywhere.
 *
 * ⚠️ A DRAFT HAS NO NUMBER, and printing the database id as if it were one is actively misleading —
 * numbering only ever happens on issue, and the number that eventually lands is not the id. So a
 * draft is named by its kind and marked as a draft; only an issued document shows a number.
 */
export function invoiceLabel(
  invoice: PlatformBillingInvoiceRow | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!invoice) return "—";
  if (invoice.invoiceNumber) return invoice.invoiceNumber;
  return t("{{kind}} (not yet numbered)", { kind: t(invoiceKindLabel(invoice.kind)) });
}

export function invoiceKindLabel(kind: PlatformBillingInvoiceRow["kind"]): string {
  switch (coerceInvoiceKind(kind)) {
    case "Subscription":
      return "Subscription";
    case "Renewal":
      return "Renewal";
    case "SeatTrueUp":
      return "Seat true-up";
    case "PlanChange":
      return "Plan change";
    case "Manual":
      return "Raised by hand";
    default:
      return "Unknown";
  }
}

export function invoiceStatusLabel(status: PlatformBillingInvoiceRow["status"]): string {
  switch (coerceInvoiceStatus(status)) {
    case "Draft":
      return "Draft";
    case "Issued":
      return "Issued";
    case "Paid":
      return "Paid";
    case "PartiallyRefunded":
      return "Partly refunded";
    case "Refunded":
      return "Refunded";
    case "Void":
      return "Void";
    case "Uncollectible":
      return "Written off";
    default:
      return "Unknown";
  }
}

const chipBase =
  "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider";

/**
 * Colour carries ONE meaning here: does this document still need something done to it.
 *
 * Issued-and-owing is the only amber, because it is the only state with an action attached. Paid,
 * refunded and written-off are all settled from the operator's point of view and read as neutral —
 * tinting them differently would turn the register into a colour puzzle instead of a queue.
 */
export function invoiceStatusChip(status: PlatformBillingInvoiceRow["status"]): string {
  switch (coerceInvoiceStatus(status)) {
    case "Issued":
      return `${chipBase} bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300`;
    case "Paid":
      return `${chipBase} bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300`;
    case "Draft":
      return `${chipBase} border border-dashed border-border text-muted-foreground`;
    default:
      return `${chipBase} bg-muted text-muted-foreground`;
  }
}

/**
 * CO-144 — the dunning ladder's own stages, in operator language.
 *
 * ⚠️ These are NOT access states, even though three of the words are shared. A stage describes how far
 * an UNPAID INVOICE has climbed; the access state is the entitlement that climb produced. So the wording
 * here names the consequence for the tenant rather than repeating the enum, which is what an operator on
 * a support call actually has to say out loud.
 */
export function dunningStageLabel(stage: string | null | undefined): string {
  switch (stage) {
    case "Grace":
      return "In grace, still fully working";
    case "Restricted":
      return "Restricted to read and export";
    case "Dormant":
      return "Dormant, HR and Admin sign-in only";
    case "Recovered":
      return "Recovered";
    case "WrittenOff":
      return "Written off";
    default:
      return "Unknown stage";
  }
}

/**
 * Colour is severity, and only severity: how much of the product the tenant has lost. Grace is amber
 * because the clock is running but nothing is broken yet; Restricted and Dormant are red because the
 * customer is already losing the product. Recovered and written-off are terminal and read neutral —
 * there is nothing left to chase, so tinting them would only compete with the rows that need action.
 */
export function dunningStageChip(stage: string | null | undefined): string {
  switch (stage) {
    case "Grace":
      return `${chipBase} bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300`;
    case "Restricted":
    case "Dormant":
      return `${chipBase} bg-red-100 text-red-900 dark:bg-red-950/50 dark:text-red-300`;
    default:
      return `${chipBase} bg-muted text-muted-foreground`;
  }
}

export function taxTreatmentLabel(treatment: string | null | undefined): string {
  switch (treatment) {
    case "UaeStandard5":
      return "UAE VAT";
    case "KsaStandard15":
      return "KSA VAT";
    case "ZeroRatedExport":
      return "Zero-rated export";
    case "OutOfScope":
      return "Out of scope";
    default:
      return "Not resolved";
  }
}

/**
 * The audit `kind` written by `WriteOverrideAuditAsync`, turned into a sentence.
 *
 * ⚠️ THESE ARE SERVER CODES, NOT DISPLAY TEXT. Rendering `entry.kind` directly — as this timeline
 * did before — prints `TAX_COUNTRY_CHANGED` at the operator in every language. The codes are a
 * stable contract, so a map is the right shape; the fallback deliberately keeps the raw code rather
 * than hiding it, because an unrecognised operator action must stay READABLE, never swallowed.
 *
 * Kept in sync by hand with the `WriteOverrideAuditAsync(companyId, "...")` call sites under
 * `Mowazaf.Server/Services/Billing/`. Adding one there without adding it here is a visible defect,
 * not a silent one, which is the intended trade.
 */
export function operatorActionLabel(kind: string): string {
  switch (kind) {
    case "ACCESS_STATE":
      return "Access granted temporarily";
    case "AUTO_RENEW":
      return "Auto-renew changed";
    case "CANCELLED":
      return "Subscription cancelled";
    case "GOODWILL_CREDIT":
      return "Account credit issued";
    case "INVOICE_CORRECTED":
      return "Invoice credited back";
    case "MANUAL_INVOICE":
      return "Invoice raised by hand";
    case "INVOICE_MARKED_PAID":
      return "Payment recorded by hand";
    case "MANUAL_REFUND":
      return "Refund issued by hand";
    case "PLAN_CHANGED":
      return "Plan changed";
    case "RECONCILED":
      return "Re-read from the provider";
    case "SEATS_CHANGED":
      return "Committed seats changed";
    case "TAX_COUNTRY_CHANGED":
      return "Tax country changed";
    case "TERM_CHANGED":
      return "Term changed";
    case "BESPOKE_ON":
      return "Converted to manual invoicing";
    case "BESPOKE_OFF":
      return "Returned to self-serve billing";
    case "MANAGEMENT_TAKEOVER":
      return "Taken over by Mowazaf";
    case "MANAGEMENT_HANDBACK":
      return "Handed back to the customer";
    case "NEVER_EXPIRES_SET":
      return "Set to never expire";
    case "NEVER_EXPIRES_CLEARED":
      return "Normal expiry resumed";
    default:
      return kind;
  }
}
